/**
 * Opening a map from its link (docs/plans/2026-10-28-link-sharing.md,
 * "Redesign": the link is the access).
 *
 * A link (`<server>/join#…`) opened in the browser: at start the app takes it
 * out of the address bar (its secret does not stay there) and keeps it for
 * this tab. Signed in (or once they are: sign-in, a new account), the map is
 * checked (engine.sharing.link.open: its key log, anchored on the owner the
 * link names), kept in their maps "via link" (not as a member), and opened.
 * Signed out, it opens for this page only, with a bar offering to sign in
 * to keep it (the link stays waiting for this tab until then).
 */
import { engine } from "../../../engine/index.js";
import { showToast } from "./toast.js";
import { alert as alertDialog } from "./modals.js";
import { on, Events } from "../../../engine/core/events.js";
import { placeBelowHeader } from "./banner-position.js";
import { t } from "../../../shared/i18n/index.js";

const KEY = "orbital-pending-link";

function remember(text) {
  try { sessionStorage.setItem(KEY, text); } catch { /* private mode: the link is lost on reload */ }
}

/** The link waiting for this tab, if any. */
export function pendingLink() {
  try { return sessionStorage.getItem(KEY); } catch { return null; }
}

function forget() {
  try { sessionStorage.removeItem(KEY); } catch { /* nothing kept */ }
}

/**
 * At start: a link in the address (`/join#…`) is taken out of it and kept
 * for this tab. @returns {boolean} whether there was one
 */
export function captureLinkFromAddress() {
  const { pathname, hash, search } = window.location;
  if (!/\/join\/?$/.test(pathname) || !hash) return false;
  const text = hash.slice(1);
  history.replaceState(null, "", pathname.replace(/join\/?$/, "") + search);
  if (!engine.sharing.link.isLink(text)) {
    showToast(t("share.link.error.invalid"), { type: "error", duration: 6000 });
    return false;
  }
  remember(text);
  return true;
}

/** The sign-in (or sign-up) screen says why the person is there (a link waits). */
export function showSignInReason(screenId = "login-screen") {
  const box = document.querySelector(`#${screenId} .auth-box`);
  if (!box) return;
  let note = box.querySelector(".auth-link-note");
  if (!pendingLink()) {
    note?.remove();
    return;
  }
  if (!note) {
    note = document.createElement("p");
    note.className = "auth-link-note";
    box.querySelector(".auth-logo")?.after(note);
  }
  note.textContent = t("share.link.open.signIn");
}

/**
 * Signed in, the vault loaded: the waiting link, if any, is opened: the map
 * checked, kept in this account's maps "via link" (a member's own map opens
 * as such), and shown. `openMap(mapId)` opens it.
 */
export async function openPendingLink({ openMap }) {
  const text = pendingLink();
  if (!text) return;
  forget();
  removePageBar();
  for (const screen of ["login-screen", "signup-screen"]) showSignInReason(screen);
  // Shown while the map is checked, then always replaced by what happened.
  showToast(t("share.link.open.opening"), { duration: 60000 });
  console.info("[Link] Opening a map's link");
  let mapId;
  let title;
  let memberRole;
  try {
    const opened = await engine.sharing.link.open(text);
    ({ title, memberRole } = opened);
    if (memberRole && !engine.maps.get(opened.mapId)) {
      console.warn("[Link] A map shared with this account, not in its maps yet:", opened.mapId);
      showToast(t("share.link.open.member"), { duration: 6000 });
      return;
    }
    mapId = await opened.keep();
  } catch (error) {
    console.warn("[Link] The link did not open:", error.code || error.status || "", error.message);
    showToast(error.message || t("share.link.error.openFailed"), { type: "error", duration: 8000 });
    return;
  }
  const name = title || engine.maps.get(mapId)?.title || t("share.invite.defaultTitle");
  // A map of this account's (its own, or shared with it): opened as such.
  // Else kept in its maps "via link", or (already there otherwise: a former
  // member's copy) open for this page only.
  let outcome;
  if (memberRole) {
    outcome = memberRole === "owner" ? "own" : "member";
    showToast(t(memberRole === "owner" ? "share.link.open.own" : "share.link.open.yours", { title: name }), { duration: 6000 });
  } else if (engine.maps.get(mapId)?.viaLink) {
    outcome = "kept";
    showToast(t("share.link.open.kept", { title: name }), { type: "success", duration: 6000 });
  } else {
    outcome = "page";
    showToast(t("share.link.open.page", { title: name }), { duration: 6000 });
  }
  console.info("[Link] Opened:", outcome, mapId);
  // Not opened again when it is the map on screen already.
  if (engine.map.id() !== mapId) await openMap(mapId);
}

/**
 * The link the open map was opened by no longer works (turned off,
 * replaced, the map deleted): said once per map, with what remains (the
 * copy on this device, read only; duplicate or export it from the maps).
 */
export function watchLinkGone({ getCurrentDocId }) {
  const told = new Set();
  return on(Events.SHARE_LINK_GONE, ({ docId }) => {
    if (told.has(docId) || getCurrentDocId() !== docId) return;
    told.add(docId);
    // Kept in the account's maps (duplicate or export it there), or open for
    // this page only (readable until the page closes).
    const kept = engine.sharing.link.held(docId)?.kept;
    void alertDialog({ title: t("share.link.gone.title"), message: t(kept ? "share.link.gone.text" : "share.link.gone.textPage") });
  });
}

const BAR_ID = "link-page-banner";

function removePageBar() {
  document.getElementById(BAR_ID)?.remove();
}

/**
 * Signed out, a link waits: the map opens for this page only (nothing kept on
 * this device), with a bar saying so and offering to sign in, which keeps
 * it in the account's maps (openPendingLink, after signing in). `openMap`
 * opens it; `signIn` shows the sign-in screen. Resolves with whether a map
 * opened.
 */
export async function openPendingLinkForPage({ openMap, signIn }) {
  const text = pendingLink();
  if (!text) return false;
  showToast(t("share.link.open.opening"), { duration: 60000 });
  console.info("[Link] Opening a map's link, not signed in");
  let opened;
  let mapId;
  try {
    opened = await engine.sharing.link.open(text);
    mapId = await opened.keepForPage();
  } catch (error) {
    forget();
    console.warn("[Link] The link did not open:", error.code || error.status || "", error.message);
    showToast(error.message || t("share.link.error.openFailed"), { type: "error", duration: 8000 });
    return false;
  }
  console.info("[Link] Opened for this page:", mapId);
  await openMap(mapId);
  showToast(t("share.link.open.page", { title: opened.title || t("share.invite.defaultTitle") }), { duration: 4000 });
  removePageBar();
  const bar = document.createElement("div");
  bar.id = BAR_ID;
  bar.className = "sibling-logout-banner";
  bar.setAttribute("role", "status");
  const message = document.createElement("span");
  message.className = "sibling-logout-banner-message";
  message.textContent = t("share.link.page.banner", {
    title: opened.title || t("share.invite.defaultTitle"),
    role: t(`share.roleInText.${opened.role === "editor" ? "editor" : "viewer"}`),
  });
  const button = document.createElement("button");
  button.type = "button";
  button.className = "sibling-logout-banner-action";
  button.textContent = t("share.link.page.signIn");
  button.addEventListener("click", () => signIn());
  bar.append(message, button);
  document.body.appendChild(bar);
  placeBelowHeader(bar);
  // Gone with the map: another map opened, or the link stopped working.
  const offOpened = on(Events.QUOTA_MAP_OPENED, ({ docId }) => {
    if (docId !== mapId) {
      removePageBar();
      offOpened();
    }
  });
  return true;
}
